import moment from 'moment';
import { randAvatarColorFullname } from 'utils/avatar';
import { flatten } from 'lodash';
const activityTypes = Object.freeze({
    task: 'tasks',
    event: 'calendar-events',
    phonecall: 'phone-calls',
});

const activityLabel = (metas, type, labelId) => {
    return labelId ? metas[type].labels.find((label) => label.id === parseInt(labelId)) : {};
};
const activityStaff = (metas, ownerId, participants) => {
    const staffList = [];
    const owner = metas.authors.find((author) => author.id === ownerId);

    for (const person of participants.filter((person) => person.type === 'staff')) {
        const personData = metas.authors.find((author) => author.id === person.id);
        if (personData && person.id !== ownerId) {
            staffList.push(personData);
        }
    }
    //put owner at top of list
    if (owner) {
        staffList.unshift(owner);
    }

    return staffList
        .filter((person) => person)
        .map((person) => {
            const name = `${person.firstname} ${person.lastname}`;
            let stateBadge = person.status === 'blocked' ? 'deactivated' : 'default';
            if (person.id === ownerId && staffList.length > 1 && person.status !== 'blocked') {
                stateBadge = 'owner';
            }
            return {
                initials: `${person.firstname[0]}${person.lastname[0]}`,
                fullname: name,
                photo: person.avatar ?? '',
                stateBadge,
                avatarColorType: randAvatarColorFullname(name),
                id: person.id,
            };
        });
};

const getActivityType = (event) => {
    switch (event) {
        case 'agendaEventLink':
            return 'event';
    }

    return event;
};

const isActivityDone = (activityType, status, isActivityLate) => {
    return (
        (activityType === 'event' && isActivityLate) ||
        (activityType === 'task' && status === 'complete') ||
        (activityType === 'phonecall' && status !== 'scheduled')
    );
};

const formatActivities = (activities, metas) => {
    return activities.data.map((activity) => {
        const isActivityLate = moment(activity.due_date).isBefore();
        const activityType = getActivityType(activity.object.type);
        // @todo on pourra surement se baser sur les filtres plutôt ? ou exposer cette info coté back
        const isDone = isActivityDone(activityType, activity.event_details.status, isActivityLate);

        const owner = activity.event_details.owner ?? activity.object.model?.owner ?? null;
        const related =
            activity.related?.map((relatedItem) => {
                return {
                    ...relatedItem,
                    subtype:
                        activity.object?.model?.related.find(
                            (_related) =>
                                _related.type === 'individual' && _related.id === relatedItem.id,
                        )?.type ?? '',
                };
            }) ?? [];
        return {
            id: activity.id,
            activityId: activity.object.id,
            isLate: isActivityLate && !isDone,
            type: activityType,
            date: activity.due_date,
            related,
            label: activityLabel(metas, activityType, activity.event_details?.label),
            staff: owner
                ? activityStaff(metas, owner?.id, activity.event_details.participants ?? [])
                : [],
            status: { done: isDone, pending: false },
            title: activity.object.model?.title ?? '',
            description: activity.object.model?.description ?? '',
            ownerId: owner?.id,
            //mockup
            prioritisation: {
                priority: activity?.object?.model?.priority ?? 0,
                pending: false,
            },
            acl: activity._embed.acl ?? { can_be_updated: false, can_be_deleted: false },
        };
    });
};

const fetchActivitiesCrmMetas = async () => {
    return ApiClient.get('activities/crm/metas');
};
const fetchDealsActivities = async (metas, params = {}, filters = {}, signal = undefined) => {
    return new Promise((resolve, reject) => {
        ApiClient.post('/activities/deals/search', params, { filters }, signal)
            .then((activities) =>
                resolve({
                    activities: formatActivities(activities.data, metas),
                    pagination: activities.data.pagination,
                }),
            )
            .catch((error) => reject(error));
    });
};

const fetchDealsActivitiesInit = ({ params, filters = {} }) => {
    return ApiClient.post('/activities/deals/search', params, { filters });
};

const fetchActivityDetail = async ({ type, id, related }, signal = undefined) => {
    const embed = ['owner', 'related', 'acl'];
    switch (type) {
        case 'task':
            embed.push('assigned_staffs');
            break;
        case 'event':
            embed.push('participants');
            break;
        case 'phonecall':
            break;
        default:
            throw new Error();
    }
    const data = {};

    data[`${activityTypes[type]}.${id}`] = {};

    const { requests, keys } = forgeRequest({
        uri: `/${activityTypes[type]}/${id}`,
        query: { embed },
        related,
    });
    keys.unshift(`${activityTypes[type]}.${id}`);

    const res = await ApiBatchClient.post(
        '/batch',
        {},
        flatten(requests).map(JSON.stringify).join('\n'),
        signal,
    );
    for (const key of keys) {
        if (typeof key !== 'string') {
            continue;
        }

        const index = requests.findIndex((request) =>
            request[0].uri.includes(key.replace('.', '/')),
        );
        if (res.data.responses[index].code === 200) {
            data[key] =
                res.data.responses[index].response.data ?? res.data.responses[index].response;
        }
    }
    return data;
};

const forgeRequest = ({ uri, query, related }) => {
    const keys = [];
    const requests = [
        [
            {
                uri: uri,
                method: 'GET',
                query: query,
            },
            {},
        ],
    ];
    for (const relatedElement of related) {
        const { request, key } = getRelatedBatchRequest(relatedElement);
        requests.push(request);
        keys.push(key);
    }
    return { requests, keys };
};
const getRelatedBatchRequest = (relatedElement) => {
    let response = null;
    if (
        relatedElement.is_individual ||
        relatedElement.subtype === 'individual' ||
        relatedElement.type === 'individual'
    ) {
        response = {
            request: [
                {
                    uri: `/individuals/${relatedElement.id}`,
                    method: 'GET',
                    query: { embed: ['invoicing_address', 'main_contact'] },
                },
                {},
            ],
            key: `individuals.${relatedElement.id}`,
        };
    } else {
        switch (relatedElement.type) {
            case 'opportunity':
                response = {
                    request: [
                        {
                            uri: `/opportunities/${relatedElement.id}`,
                            method: 'GET',
                            query: {
                                embed: ['company', 'contacts'],
                            },
                        },
                        {},
                    ],
                    key: `opportunities.${relatedElement.id}`,
                };
                break;
            case 'company':
            case 'third':
            case 'client':
            case 'supplier':
            case 'prospect':
                response = {
                    request: [
                        {
                            uri: `/companies/${relatedElement.id}`,
                            method: 'GET',
                            query: { embed: ['invoicing_address', 'main_contact'] },
                        },
                        {},
                    ],
                    key: `companies.${relatedElement.id}`,
                };
                break;
            case 'contact':
            case 'people':
                const id = Number.parseInt(relatedElement.id ?? relatedElement.peopleid);
                response = {
                    request: [
                        {
                            uri: `/contacts/${id}`,
                            method: 'GET',
                        },
                        {},
                    ],
                    key: `contacts.${id}`,
                };
                break;
            case 'estimate':
                response = {
                    request: [
                        {
                            uri: `/estimates/${relatedElement.id}`,
                            method: 'GET',
                        },
                        {},
                    ],
                    key: `estimates.${relatedElement.id}`,
                };
                break;
            case 'invoice':
                response = {
                    request: [
                        {
                            uri: `/invoices/${relatedElement.id}`,
                            method: 'GET',
                        },
                        {},
                    ],
                    key: `invoices.${relatedElement.id}`,
                };
                break;
            case 'creditnote':
                response = {
                    request: [
                        {
                            uri: `/credit-notes/${relatedElement.id}`,
                            method: 'GET',
                        },
                        {},
                    ],
                    key: `credit-notes.${relatedElement.id}`,
                };
                break;
            case 'order':
                response = {
                    request: [
                        {
                            uri: `/orders/${relatedElement.id}`,
                            method: 'GET',
                        },
                        {},
                    ],
                    key: `orders.${relatedElement.id}`,
                };
                break;
            default:
                response = {};
        }
    }
    if (!response) {
        throw new Error('Invalid related');
    }
    return response;
};
const updateActivity = async (type, activityId, payload) => {
    return ApiClient.put(`/${activityTypes[type]}/${activityId}`, {}, payload);
};

const delActivity = (type, activityId) => {
    return ApiClient.del(`${activityTypes[type]}/${activityId}`);
};

const updateActivitiesDashboardCardFilters = (filters, cardName) => {
    return axios.post(`/home/card/settings/save/${cardName}`, { settings: filters });
};

const searchActivities = async (params, filters = {}) => {
    return ApiClient.post('/activities/search', params, {
        filters,
    });
};

const exportDealsActivities = (params, body) => {
    return ApiClient.post('/activities/deals/export', params, body);
};

export {
    fetchDealsActivities,
    fetchActivitiesCrmMetas,
    fetchActivityDetail,
    updateActivity,
    delActivity,
    fetchDealsActivitiesInit,
    formatActivities,
    isActivityDone,
    updateActivitiesDashboardCardFilters,
    getRelatedBatchRequest,
    searchActivities,
    exportDealsActivities,
};
